import {
  QBox,
  QButton,
  QCheckbox,
  QCloseButton,
  QDivider,
  QFormControl,
  QIcon,
  QInput,
  QLink,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QSelectItem,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUmAnalytics } from '../../analytics';
import { UserRoleInfoLink } from '../../constants';
import { InviteUserRequest, ModalComponentProps } from '../../types';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';
import { UsergroupsList } from '../../utils/umfe.enums';
import { EMAIL_MAX_LENGTH, validateEmail } from '../../utils/validation/email';
import { isMedtechRole } from '../../types/medtechRole.types';
import { ErrorCodes, getErrorMessageOrDefault } from '../../errors/um-errors';
import { isAxiosError } from '../../utils/validation/axios';
import { inviteUserInMedtech, isFormerUserCompany } from '../../api/medtech-api';

type InviteUserFormModalProps = ModalComponentProps & {
  companyId: number;
  getUsers: () => Promise<void>;
  unlimitedBasicUsers?: boolean;
};

export const InviteUserFormModal = ({
  closeModal,
  companyId,
  getUsers,
  unlimitedBasicUsers = false,
}: InviteUserFormModalProps) => {
  const { t } = useTranslation();
  const analytics = useUmAnalytics();
  const isInit = useRef(true);

  const usergroups = useMemo(
    () =>
      Object.values(UsergroupsList).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );

  // InviteUserForm fields
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(UsergroupsList.BASIC.value); // Default to Basic user role
  const [isAdmin, setAdminAccess] = useState(false);
  const [isBilling, setBillingAccess] = useState(false);

  const [showFormerUserContent, setShowFormerUserContent] = useState(false);
  const [inviteButtonLabel, setInviteButtonLabel] = useState(t('modals.inviteUser.sendInvitation'));

  // Track Validation
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isFormValid, setFormValid] = useState(false);

  // Track API
  const [isLoading, setLoading] = useState(false);

  const validateEmailValue = () => {
    const isValidEmailBool = validateEmail(email);
    setIsValidEmail(isValidEmailBool);
    setFormValid(isValidEmailBool);
  };

  const { showToast } = useToastProvider();

  // Validate form when email change
  useEffect(() => {
    if (isInit.current) {
      isInit.current = false;
      return;
    }
    validateEmailValue();
  }, [email]);

  useEffect(() => {
    const buttonLabel = showFormerUserContent
      ? t('modals.inviteUser.resendInvitation')
      : t('modals.inviteUser.sendInvitation');
    setInviteButtonLabel(buttonLabel);
  }, [showFormerUserContent, t]);

  const handleSubmit = async () => {
    const isFormer = await isFormerUserCompany(companyId, email);

    // If user was part of company and currently not showing the content for former users
    if (isFormer && !showFormerUserContent) {
      setShowFormerUserContent(true);
      return;
    }

    setLoading(true);

    const payload: InviteUserRequest = {
      email,
      role,
      isAdmin,
      isBilling,
    };

    try {
      await inviteUserInMedtech(companyId, payload);

      await getUsers();
      showToast(createSuccessToast(t('messages.success.invitationSent'), 'invite-user-send-success-toast'));
      analytics.inviteUserSuccess(isAdmin, { role });
      setLoading(false);
      closeModal();
    } catch (error: unknown) {
      setLoading(false);

      const isUnlimitedBasicInvite = unlimitedBasicUsers && role === UsergroupsList.BASIC.value;

      const errorMessageMap = {
        [ErrorCodes.ALREADY_ACTIVE_USER]: t('messages.validation.userAlreadyExists', { email }),
        [ErrorCodes.ACTIVE_USERS_LIMIT_REACHED]: t('messages.validation.activeUsersLimit'),
        ...(isUnlimitedBasicInvite
          ? {}
          : { [ErrorCodes.TOTAL_USERS_LIMIT_REACHED]: t('messages.validation.totalUsersLimit') }),
        [ErrorCodes.INVALID_EMAIL]: t('messages.validation.invalidEmail'),
      };

      const errorMessage = getErrorMessageOrDefault(error, errorMessageMap, t('messages.error.sendInvitation'));

      showToast(createErrorToast(errorMessage, 'invite-user-send-error-toast'));

      const status = isAxiosError(error) ? error?.response?.status : undefined;
      analytics.inviteUserFailed(isAdmin, { role, status });
    }
  };

  const setAdminAccessValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdminAccess(event.target.checked);
  };

  const setBillingAccessValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBillingAccess(e.target.checked);
  };

  const setRoleValue = (e: QSelectItem) => {
    const { value } = e;
    if (isMedtechRole(value)) {
      setRole(value);
    }
  };

  const setEmailValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);

    if (showFormerUserContent) {
      setShowFormerUserContent(false);
    }
  };

  return (
    <QModal onClose={closeModal} size="2xl" isOpen trapFocus={false}>
      <QModalHeader>
        <QText data-cy="heading">{t('modals.inviteUser.title')}</QText>
        <QCloseButton onClick={closeModal} />
      </QModalHeader>
      <QModalBody>
        <form id="inviteUserForm" aria-label="inviteUserForm" onSubmit={handleSubmit}>
          <QBox pb="2">
            <QFormControl helper={t('forms.helperText.inviteUser')} label={t('forms.labels.emailAddress')} size="sm">
              <QInput
                type="email"
                data-cy="email-input"
                data-intercom-target="email-input"
                placeholder={t('forms.labels.emailAddress')}
                aria-label="email"
                maxLength={EMAIL_MAX_LENGTH}
                value={email}
                onChange={setEmailValue}
                isInvalid={!isValidEmail}
              />
            </QFormControl>
          </QBox>
          <QBox pb="2">
            <QFormControl data-testid="roleSelectFormControl" label={t('forms.labels.userRole')} size="sm">
              <QSelect
                aria-label="role"
                data-cy="user-role-select"
                data-intercom-target="user-role-select"
                options={usergroups}
                value={role}
                onChange={(item) => item && setRoleValue(item)}
              />
              <QLink data-cy="learn-more-link" isExternal href={UserRoleInfoLink}>
                {t('forms.helperText.learnMoreRoles')}
              </QLink>
            </QFormControl>
          </QBox>
          <QBox pb="2">
            <QFormControl size="sm" helper={t('forms.helperText.adminAccess')}>
              <QCheckbox
                data-cy="admin-access"
                data-intercom-target="admin-access-checkbox-"
                aria-label="admin access"
                onChange={setAdminAccessValue}
              >
                {t('forms.labels.adminAccess')}
              </QCheckbox>
            </QFormControl>
          </QBox>
          <QBox pb="2">
            <QFormControl size="sm">
              <QCheckbox
                data-cy="billing-access"
                data-intercom-target="billing-access-checkbox"
                aria-label="billing access"
                onChange={setBillingAccessValue}
              >
                {t('forms.labels.billingAccess')}
              </QCheckbox>
            </QFormControl>
          </QBox>
          <QDivider orientation="horizontal" />
        </form>
        {showFormerUserContent && (
          <QBox p="2" color="InactiveCaptionText">
            <QText>
              <QIcon iconName="AlertTriangle" /> {t('modals.inviteUser.reinviteMessage', { email })}
            </QText>
          </QBox>
        )}
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-button" variant="outline" onClick={closeModal}>
          {t('common.cancel')}
        </QButton>
        <QButton
          data-cy="send-invitation-button"
          data-intercom-target="send-invitation-button-"
          type="submit"
          isLoading={isLoading}
          onClick={handleSubmit}
          isDisabled={!isFormValid}
        >
          {inviteButtonLabel}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
